import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { basisLabel, fraction, fromNet, rateFor, type TaxRate, toCents, toNet, toNumber } from "../../shared/money";
import type { Period } from "../../shared/periods";
import { api } from "../../web/lib/api";
import { useCompany } from "../../web/lib/company";
import { useSm8Query } from "../../web/lib/queries";
import { inOrder, whileSaving } from "../../web/lib/saving";
import type { ListEntry } from "./itemTags";
import { type Change, flushChanges, queueChange } from "./liveRows";
import type {
    BundleLine,
    BundleRow,
    ChangeRow,
    CheckedKind,
    CostSource,
    ItemState,
    ItemStats,
    JobTimePreview,
    JobTimeSettings,
    MaterialPatch,
    MaterialRow,
    MaterialUpdate,
    PriceHistoryEntry,
    PackagePart,
    PriceListAction,
    PriceListData,
    PriceListSettings,
    MarkupTier,
    GroupRow,
    GroupStats,
    GroupTax,
    GroupTaxBasis,
    PricingRule,
    SupplierDetails,
    SupplierPatch,
    StatsResponse,
    SupplierPriceRow,
    SupplierRow,
    SupplierStats,
    TaxQuote,
    TrackedObject,
} from "./meta";
import { withShortNames } from "./meta";
import { applyNameChange, displayName } from "./names";
import { smartName, smartTagsOf, tagsOf } from "./smartTags";
import { type ItemRate, samePrice } from "./pricing";
import { groupOutOfDate } from "./attention";
import { byRealCost, costFromQuoted, costPrice, itemCost, quotedPrice, quoteOf, shownTypedCost } from "./supplierPricing";

/**
 * Everything for the Price List, kept up to date live (from anyone, or ServiceM8): record by record
 * for items, suppliers and bundles (liveRows.ts), whole for anything else (e.g. tax rates).
 */
export function usePriceList() {
    const company = useCompany();
    const qc = useQueryClient();
    return useSm8Query<PriceListData>({
        companyId: company.id,
        objects: ["material", "materialbundle", "taxrate", "supplier", "vendor"],
        key: ["pricelist"],
        path: "/tools/pricelist",
        fresh: true,
        onChange: (change) => queueChange(qc, company.id, change),
    });
}

/** Whether this person can do `action` (PRICELIST_ACTIONS): their role allows it (Settings › Access) and ServiceM8 is connected. */
export const can = (data: Pick<PriceListData, "whyNot">, action: PriceListAction) => data.whyNot[action] === null;

/** Why they can't do `action`, for a notice, or null if they can. */
export const whyNot = (data: Pick<PriceListData, "whyNot">, action: PriceListAction) => data.whyNot[action];

/** A record's history (history.ts): its changes here and in ServiceM8, newest first. */
export function useHistory(object: TrackedObject, uuid: string) {
    const company = useCompany();
    return useSm8Query<ChangeRow[]>({ companyId: company.id, objects: [object, "staff"], key: ["pricelist", "history", object, uuid], path: `/tools/pricelist/history/${object}/${uuid}` });
}

/** Stats (stats.ts) are worked out from jobs and their lines, and items. */
const STATS_OBJECTS = ["job", "jobmaterial", "jobactivity", "material"] as const;

/** Stats for a period (days both included), or the last 12 months without one. `fresh`: jobs read afresh first (a Stats tab). */
function useStats<T>(kind: string, path: string, id: string, period?: Period, enabled = true, fresh = true) {
    const company = useCompany();
    return useSm8Query<StatsResponse<T>>({
        companyId: company.id,
        objects: [...STATS_OBJECTS],
        key: ["pricelist", "stats", kind, id, period?.from ?? "", period?.to ?? ""],
        path: `/tools/pricelist/${path}${period ? `?from=${period.from}&to=${period.to}` : ""}`,
        fresh,
        enabled,
    });
}

/** How an item is used on jobs (its Stats tab). `fresh` false: as the jobs are now (no reading them afresh just for a hint). */
export const useItemStats = (uuid: string, period?: Period, enabled = true, fresh = true) => useStats<ItemStats>("item", `materials/${uuid}/stats`, uuid, period, enabled, fresh);
/** A supplier's prices, and what's likely bought from them (its Stats tab). */
export const useSupplierStats = (uuid: string, period?: Period) => useStats<SupplierStats>("supplier", `suppliers/${uuid}/stats`, uuid, period);
/** How a group's items are used on jobs (its Stats tab). */
export const useGroupStats = (id: string, period?: Period) => useStats<GroupStats>("group", `groups/${id}/stats`, id, period);


/**
 * What a draft of the job time settings would give (being tried before it's saved); none without one. `savedAt`: when the saved
 * settings' preview was fetched: worked out again with it (jobs changed), so the two compare like for like.
 */
export function useJobTimeDraftPreview(draft: JobTimeSettings | null, savedAt: number) {
    const company = useCompany();
    return useQuery({
        queryKey: ["c", company.id, "pricelist", "job-time", "draft", draft, savedAt],
        queryFn: () => api<JobTimePreview>(`/c/${company.id}/tools/pricelist/job-time/preview`, { body: { jobTime: draft } }),
        enabled: draft !== null,
        // The last draft's stays while the next is worked out (no blank, no jump).
        placeholderData: keepPreviousData,
    });
}

/** What the job time settings give (Price List settings' preview): every item's time on jobs. */
export function useJobTimePreview(enabled: boolean) {
    const company = useCompany();
    return useSm8Query<JobTimePreview>({
        companyId: company.id,
        objects: [...STATS_OBJECTS, "company"],
        key: ["pricelist", "job-time"],
        path: "/tools/pricelist/job-time",
        enabled,
    });
}

/** An item's tax rate (its own, else the default): the rate, its uuid, and as a fraction. */
export function itemTax(data: Pick<PriceListData, "taxRates">, m: Pick<MaterialRow, "tax_rate_uuid">): ItemRate & { rate: TaxRate | null } {
    const rate = rateFor(m.tax_rate_uuid, data.taxRates);
    return { rate, uuid: rate?.uuid ?? "", tax: fraction(rate) };
}

/**
 * How lists show costs, to compare them: before tax, as a business that claims tax back on purchases
 * pays them (Price List settings), else with it (it's then part of the cost). Not the item's own basis, so a cost reads the
 * same whatever its price includes. (Its window shows it as it was typed: QuotedAmount.tsx.)
 */
export const costIncludesTax = (data: Pick<PriceListData, "settings">) => !data.settings.purchaseTaxClaimed;

/** An item's cost as it's shown (see costIncludesTax), with its label ("ex GST"). */
export function shownCost(data: Pick<PriceListData, "settings" | "taxRates">, m: Pick<MaterialRow, "cost" | "price_includes_taxes" | "tax_rate_uuid">) {
    const { rate, tax } = itemTax(data, m);
    const inc = costIncludesTax(data);
    return { amount: fromNet(toNet(toNumber(m.cost), m.price_includes_taxes, tax), inc, tax), label: basisLabel(inc, rate), fullLabel: basisLabel(inc, rate, true) };
}

/** A group's tax with its rate as a fraction, and the rate itself (for labels). */
export function groupTaxBasis(data: Pick<PriceListData, "taxRates">, tax: GroupTax): GroupTaxBasis & { rate: TaxRate | null } {
    const rate = rateFor(tax.rateUuid, data.taxRates);
    return { ...tax, rate, tax: fraction(rate) };
}

/** The group that prices a product, if any (one it overrides doesn't: memberOf). */
export const groupOf = (data: Pick<PriceListData, "itemPricing" | "groups">, uuid: string) => {
    const pricing = data.itemPricing[uuid];
    return pricing?.groupId && !pricing.overridesGroup ? data.groups.find((s) => s.id === pricing.groupId) : undefined;
};

/** The group a product is in, if any (priced by it, or overriding it). */
export const memberOf = (data: Pick<PriceListData, "itemPricing" | "groups">, uuid: string) => {
    const id = data.itemPricing[uuid]?.groupId;
    return id ? data.groups.find((s) => s.id === id) : undefined;
};

/** The name an item's tags give it (smartTags.ts), if it reads differently in its name now, and what gives it wording; else null. */
export function smartNameCheck(data: PriceListData, m: Pick<MaterialRow, "uuid" | "name">) {
    const name = smartName(data, m);
    return name === null ? null : { name, from: smartTagsOf(data, m.uuid).from };
}

/** An item's tags' names, in order (for searching it by them). */
export const tagNamesOf = (data: PriceListData, uuid: string) => tagsOf(data, uuid).map((t) => t.name);

/** An item's name as lists show it: the typed name's first line, without its tags. */
export const itemName = (data: Pick<PriceListData, "nameTags">, m: Pick<MaterialRow, "name">) => displayName(m.name, data.nameTags) || "Untitled item";

/** The products in a group (those overriding it too). */
export const membersOf = (data: PriceListData, groupId: string) => data.materials.filter((m) => data.itemPricing[m.uuid]?.groupId === groupId);

/** The products a group prices (not those overriding it). */
export const pricedMembersOf = (data: PriceListData, groupId: string) => membersOf(data, groupId).filter((m) => !data.itemPricing[m.uuid]?.overridesGroup);

/**
 * How many of a group's items' prices don't match it yet, and names their smart tags (smartTags.ts); and how many it can't
 * price (no cost: pricing.ts missingCost).
 */
export const outOfDate = (data: PriceListData, group: GroupRow) => groupOutOfDate(data, group.id);

/** A name for a new group: `name` (its first line), or with a number after it if one has it already ("Deadbolt 2"). */
export function freeGroupName(data: PriceListData, name: string) {
    const base = name.split(/\r?\n/)[0]?.trim() || "New group";
    const taken = new Set(data.groups.map((s) => s.name.trim().toLowerCase()));
    let n = 1;
    while (taken.has((n === 1 ? base : `${base} ${n}`).toLowerCase())) n++;
    return n === 1 ? base : `${base} ${n}`;
}

/**
 * Whether it's still showing (a ref, read when a long run ends): a run that outlives it (e.g. a group's updates, its window
 * since switched to another group) doesn't clear what's there now.
 */
export function useStillShown() {
    const shown = useRef(true);
    useEffect(() => {
        shown.current = true;
        return () => {
            shown.current = false;
        };
    }, []);
    return shown;
}

/** Where one item's update has got (updateEach): waiting its turn, being saved, done, or turned down (why). */
export type RowUpdate = { state: "waiting" | "updating" | "done" } | { state: "failed"; error: string };

/**
 * Runs `update` on each item one at a time (each is a ServiceM8 write), telling `onProgress` how
 * far it's got ("Updating 2 of 5 names...") and null once it's done, and `onItem` where each item
 * has got (all waiting first). One turned down doesn't stop the rest: once they're all done, it
 * rejects saying how many couldn't be updated (and the first one's reason).
 */
export async function updateEach(
    uuids: readonly string[],
    update: (uuid: string) => Promise<unknown>,
    what: string,
    onProgress: (text: string | null) => void,
    onItem?: (uuid: string, row: RowUpdate) => void,
) {
    for (const uuid of uuids) onItem?.(uuid, { state: "waiting" });
    const failed: Error[] = [];
    try {
        for (const [i, uuid] of uuids.entries()) {
            onProgress(`Updating ${i + 1} of ${uuids.length} ${what}...`);
            onItem?.(uuid, { state: "updating" });
            try {
                await update(uuid);
                onItem?.(uuid, { state: "done" });
            } catch (err) {
                const e = err instanceof Error ? err : new Error(String(err));
                failed.push(e);
                onItem?.(uuid, { state: "failed", error: e.message });
            }
        }
    } finally {
        onProgress(null);
    }
    const [first] = failed;
    if (first) throw new Error(failed.length === 1 && uuids.length === 1 ? first.message : `${failed.length} of the ${what} couldn't be updated: ${first.message}`);
}

/**
 * Where each item's updates have got (updateEach's `onItem`), by `what:uuid` ("prices:…"): for a list to mark its rows (being
 * saved, waiting, turned down) and drop those done. `track(what)` is the `onItem` for one kind; `clear` forgets them all.
 */
export function useRowUpdates() {
    const [rows, setRows] = useState<ReadonlyMap<string, RowUpdate>>(() => new Map());
    const track = useCallback((what: string) => (uuid: string, row: RowUpdate) => setRows((was) => new Map(was).set(`${what}:${uuid}`, row)), []);
    const clear = useCallback(() => setRows(new Map()), []);
    // Once a run's over: only those turned down are kept (marked till the next run); those done are in the data now.
    const settle = useCallback(() => setRows((was) => new Map([...was].filter(([, row]) => row.state === "failed"))), []);
    return { rows, track, clear, settle };
}

/**
 * Saving. Changes show straight away; saves to one item go one at a time in the order made; the
 * data is refetched once the last save in flight finishes (showing what ServiceM8 stored).
 * Rejections throw, so the field that changed can show why.
 */
export function usePriceListSaver() {
    const company = useCompany();
    const qc = useQueryClient();
    const key = ["c", company.id, "pricelist"];
    const base = `/c/${company.id}/tools/pricelist`;

    /**
     * A save: shown at once (`apply`), sent in order with others to the same `target`, then the
     * records it's about (`saved`, or "all") are fetched fresh once no save is in flight (liveRows.ts).
     */
    async function optimistic<T>(target: string, apply: ((d: PriceListData) => PriceListData) | null, request: () => Promise<T>, saved: Change) {
        return whileSaving(
            qc,
            key,
            async () => {
                if (apply) {
                    await qc.cancelQueries({ queryKey: key, exact: true });
                    const now = qc.getQueryData<PriceListData>(key);
                    if (now) qc.setQueryData(key, apply(now));
                }
                try {
                    return await inOrder(`${company.id}:${target}`, request);
                } finally {
                    queueChange(qc, company.id, saved);
                }
            },
            // The records saved, fresh; and what's shown beside them (histories, stats), which live updates skip while saving.
            () => Promise.all([flushChanges(qc, company.id), qc.invalidateQueries({ queryKey: key, predicate: (q) => q.queryKey.length > key.length })]).then(() => undefined),
        );
    }
    /** A record made here: fetched as soon as it's saved (the live update brings it to everyone else). */
    const made = <T extends { uuid: string }>(kind: "material" | "supplier" | "materialbundle", request: Promise<T>) =>
        request.then((row) => {
            queueChange(qc, company.id, { [kind]: [row.uuid] });
            return row;
        });

    const patchRow = (uuid: string, { nameChange, ...patch }: MaterialUpdate) => (d: PriceListData) => ({
        ...d,
        materials: d.materials.map((m) =>
            m.uuid === uuid ? { ...m, ...patch, ...(nameChange ? { name: applyNameChange(m.name, nameChange, d.nameTags) } : {}) } : m,
        ),
    });

    return {
        updateMaterial: (uuid: string, patch: MaterialUpdate) =>
            // Switching with/without tax rewrites the price and cost on the server: wait for those.
            optimistic(
                uuid,
                patch.price_includes_taxes === undefined ? patchRow(uuid, patch) : null,
                () => api<MaterialRow>(`${base}/materials/${uuid}`, { method: "PATCH", body: patch }),
                { material: [uuid] },
            ),
        /** For sale, hidden (archived in ServiceM8, on standby) or retired (states.ts). */
        setItemState: (uuid: string, state: ItemState) =>
            optimistic(
                uuid,
                (d) => ({ ...d, materials: d.materials.map((m) => (m.uuid === uuid ? { ...m, active: state === "sale", hidden: state === "hidden" } : m)) }),
                () => api<MaterialRow>(`${base}/materials/${uuid}/state`, { body: { state } }),
                { material: [uuid] },
            ),
        /** An item, or (`product`) a product in the Catalogue (catalogue.ts). */
        createMaterial: (data: MaterialPatch & { name: string; hidden?: boolean; product?: boolean; /** Kept for every try (useNewUuid). */ uuid?: string }) => made("material", api<MaterialRow>(`${base}/materials`, { body: data })),
        /** A new item like this one (duplicate.ts): the name and item number given, its supplier prices copied too or not. */
        duplicateItem: (uuid: string, opts: { name: string; itemNumber: string; supplierPrices: boolean; /** The copy's, kept for every try (useNewUuid). */ uuid?: string }) =>
            made("material", api<MaterialRow>(`${base}/materials/${uuid}/duplicate`, { body: opts })),
        /**
         * Save an item's own pricing as edited (its Pricing tab's draft) in one go: the rule (null: a
         * typed price), how it's taxed and the typed price if they changed, and optionally update its
         * price to what the rule gives. The draft stays on screen until the saved data is back.
         */
        saveItemPricing: (uuid: string, draft: { rule: PricingRule | null; tax?: TaxQuote; price?: string; updatePrice?: boolean }) =>
            optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/pricing`, { method: "PUT", body: draft }), { material: [uuid] }),
        updateToPricing: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/pricing/update`, { body: {} }), { material: [uuid] }),
        /** Price it here again (ServiceM8 no longer prices it itself). */
        priceHere: (uuid: string) => optimistic(uuid, null, () => api(`${base}/materials/${uuid}/price-here`, { body: {} }), { material: [uuid] }),
        /** Every item built from this product (or just `only` of them), to what its products add up to now (catalogue.ts updateBuiltFrom). */
        updateBuiltFrom: (uuid: string, only?: string[]) => api<{ updated: number; failed: number }>(`${base}/materials/${uuid}/built/update`, { body: only ? { only } : {} }),
        /** Its name to the one its tags give it. */
        updateNameToTags: (uuid: string) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/name/update-to-tags`, { method: "POST" }), { material: [uuid] }),
        /** Its tags, all at once (shown straight away); with `updateName`, its name updated to them too; or with `name`, saved together. */
        setItemTags: (uuid: string, tagIds: string[], updateName = false, name?: string) =>
            optimistic(
                uuid,
                (d) => ({
                    ...(name === undefined ? d : patchRow(uuid, { name })(d)),
                    itemTags: { ...d.itemTags, [uuid]: d.tags.filter((t) => tagIds.includes(t.id)).map((t) => t.id) },
                }),
                () => api<MaterialRow>(`${base}/materials/${uuid}/item-tags`, { method: "PUT", body: { tagIds, updateName, name } }),
                { material: [uuid] },
            ),
        // Tags (itemTags.ts)
        /** The whole list at once (Price List settings: itemTags.ts saveTagList). */
        saveTagList: (entries: ListEntry[]) => optimistic("tags", null, () => api(`${base}/tags`, { method: "PUT", body: { entries } }), "all"),
        /** Its name, item number and barcode changed in ServiceM8 (or just `fields`): put back as they were (putBack.ts), those changes seen. */
        putBackDetails: (uuid: string, fields?: string[]) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/details/put-back`, { body: fields ? { fields } : {} }), { material: [uuid] }),
        /**
         * Someone has seen these records' changes from ServiceM8 (history.ts): they stop showing as changed, straight away. `ids`:
         * the changes they were shown (only those are seen).
         */
        markSeen: (object: TrackedObject, uuids: string[], ids?: string[]) =>
            optimistic(
                `seen:${object}`,
                (d) => {
                    const seen = new Set(uuids);
                    return { ...d, changedInServiceM8: { ...d.changedInServiceM8, [object]: d.changedInServiceM8[object].filter((u) => !seen.has(u)) } };
                },
                () => api<{ ok: true }>(`${base}/history/${object}/seen`, { body: { uuids, ids } }),
                { [object]: uuids },
            ),
        /** These are real items (known.ts): they stop showing as unknown, straight away. */
        /** Checked (checked.ts): it's right as it is now; or not any more. A group's goes with everything (groups aren't records). */
        setChecked: (kind: CheckedKind, id: string, checked: boolean) =>
            optimistic(
                "checked",
                (d) => {
                    const { [id]: _, ...rest } = d.checked;
                    return { ...d, checked: checked ? { ...rest, [id]: { at: Date.now(), by: null } } : rest };
                },
                () => api<{ ok: true }>(`${base}/checked/${kind}/${id}`, { body: { checked } }),
                kind === "group" ? "all" : { [kind]: [id] },
            ),
        markKnown: (uuids: string[]) =>
            optimistic(
                "known",
                (d) => {
                    const marked = new Set(uuids);
                    return { ...d, unknownItems: d.unknownItems.filter((u) => !marked.has(u)) };
                },
                () => api<{ ok: true }>(`${base}/materials/known`, { body: { uuids } }),
                { material: uuids },
            ),
        /** Its "Cost to update": its cost, to what the supplier price it follows gives. */
        updateCost: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/cost/update`, { method: "POST" }), { material: [uuid] }),
        /** Every cost to update (or these items'), in the background: each item updates on screen as it's saved. */
        updateCosts: (uuids?: string[]) => api<{ updating: number }>(`${base}/materials/costs/update`, { body: { uuids } }),
        keepPrice: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/pricing/keep`, { method: "POST" }), { material: [uuid] }),
        /** Merge two items that are one (merge.ts): `removeUuid` into `keepUuid`, with `takeCost` its cost too. */
        mergeItems: (keepUuid: string, removeUuid: string, takeCost: boolean) =>
            optimistic(keepUuid, null, () => api<MaterialRow>(`${base}/materials/merge`, { body: { keepUuid, removeUuid, takeCost } }), "all"),
        /** Make it a labour time (a set time on an hourly labour product: labourTime.ts), or not (null: its price kept as its own). */
        setLabourTime: (uuid: string, link: { rateUuid: string; minutes: number } | null) =>
            optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/labour`, { method: "PUT", body: { link } }), { material: [uuid] }),
        /** Every item an import in ServiceM8 brought in again, merged into the one it copies (merge.ts mergeImported). */
        mergeImported: () => optimistic("", null, () => api<{ merged: number; failed: number }>(`${base}/materials/merge-imported`, { body: {} }), "all"),
        saveSettings: (patch: Partial<PriceListSettings>) =>
            optimistic(
                "settings",
                // Short names merge (only those given change), as the server saves them.
                (d) => ({ ...d, settings: { ...d.settings, ...patch, taxShortNames: patch.taxShortNames ? withShortNames(d.settings.taxShortNames, patch.taxShortNames) : d.settings.taxShortNames } }),
                () => api<PriceListSettings>(`${base}/settings`, { method: "PATCH", body: patch }),
                "all",
            ),
        /**
         * Change the job time settings: `change` is given them as they are by then (with any changes still being saved), so
         * changes made quickly one after another (two items taken out) each keep the other's. What's sent is the whole of
         * them as they are when it goes (after the saves before it).
         */
        saveJobTime: (change: (t: JobTimeSettings) => JobTimeSettings) =>
            optimistic(
                "settings",
                (d) => ({ ...d, settings: { ...d.settings, jobTime: change(d.settings.jobTime) } }),
                () => {
                    const jobTime = qc.getQueryData<PriceListData>(key)?.settings.jobTime;
                    if (!jobTime) throw new Error("The Price List is still loading. Try again in a moment.");
                    return api<PriceListSettings>(`${base}/settings`, { method: "PATCH", body: { jobTime } });
                },
                "all",
            ),

        // Suppliers
        createSupplier: (data: SupplierPatch & { name: string; /** Kept for every try (useNewUuid). */ uuid?: string }) => made("supplier", api<SupplierRow>(`${base}/suppliers`, { body: data })),
        updateSupplier: (uuid: string, patch: SupplierPatch) =>
            optimistic(
                uuid,
                (d) => ({ ...d, suppliers: d.suppliers.map((s) => (s.uuid === uuid ? { ...s, ...patch } : s)) }),
                () => api<SupplierRow>(`${base}/suppliers/${uuid}`, { method: "PATCH", body: patch }),
                { supplier: [uuid] },
            ),
        updateSupplierDetails: (uuid: string, patch: Partial<SupplierDetails>) =>
            optimistic(
                uuid,
                (d) => ({ ...d, suppliers: d.suppliers.map((s) => (s.uuid === uuid ? { ...s, details: { ...s.details, ...patch } } : s)) }),
                () => api<SupplierRow>(`${base}/suppliers/${uuid}/details`, { method: "PATCH", body: patch }),
                // How they usually quote changes how their prices read, across items: everything.
                "all",
            ),

        // Supplier prices
        addPrice: (price: Omit<SupplierPriceRow, "id" | "checkedAt" | "changedAt">) =>
            optimistic(price.materialUuid, null, () => api<SupplierPriceRow>(`${base}/prices`, { body: price }), { material: [price.materialUuid] }),
        /** `restore`: undoing a change just made, back to when it was last checked and changed. */
        updatePrice: (
            price: SupplierPriceRow,
            patch: Partial<Pick<SupplierPriceRow, "minQty" | "priceNet" | "supplierCode" | "url">> & { quote?: TaxQuote; checked?: boolean; restore?: { checkedAt: number; changedAt: number } },
        ) =>
            optimistic(
                price.materialUuid,
                (d) => ({
                    ...d,
                    supplierPrices: d.supplierPrices.map((p) => {
                        if (p.id !== price.id) return p;
                        const { restore, checked, ...fields } = patch;
                        return { ...p, ...fields, ...(checked ? { checkedAt: Date.now() } : {}), ...restore };
                    }),
                }),
                () => api<SupplierPriceRow>(`${base}/prices/${price.id}`, { method: "PATCH", body: patch }),
                { material: [price.materialUuid] },
            ),
        removePrice: (price: SupplierPriceRow) =>
            optimistic(
                price.materialUuid,
                (d) => ({ ...d, supplierPrices: d.supplierPrices.filter((p) => p.id !== price.id) }),
                () => api(`${base}/prices/${price.id}`, { method: "DELETE" }),
                { material: [price.materialUuid] },
            ),
        /**
         * A typed cost: the amount exactly as typed, and how it's taxed. Shown at once with the cost
         * that gives (`cost`, as ServiceM8 stores it), so the amount never flickers (and is shown back
         * exactly as typed: supplierPricing.ts shownTypedCost); the server works the cost out itself.
         */
        saveTypedCost: (materialUuid: string, typed: string, quote: TaxQuote, cost: string, update = true) =>
            optimistic(
                materialUuid,
                (d) => ({
                    ...d,
                    costQuotes: { ...d.costQuotes, [materialUuid]: quote },
                    costTyped: { ...d.costTyped, [materialUuid]: typed },
                    // Saved only: ServiceM8's cost stays, and the typed one's a cost to update (when it gives another).
                    ...(update
                        ? { materials: d.materials.map((m) => (m.uuid === materialUuid ? { ...m, cost } : m)) }
                        : samePrice(cost, d.materials.find((m) => m.uuid === materialUuid)?.cost ?? cost)
                          ? {}
                          : { costUpdates: { ...d.costUpdates, [materialUuid]: cost } }),
                }),
                () => api<MaterialRow>(`${base}/materials/${materialUuid}/typed-cost`, { method: "PUT", body: { ...quote, typed, update } }),
                { material: [materialUuid] },
            ),
        setCostSource: (materialUuid: string, source: CostSource) =>
            optimistic(
                materialUuid,
                (d) => withCostSource(d, materialUuid, source),
                () => api(`${base}/materials/${materialUuid}/cost-source`, { method: "PUT", body: source }),
                { material: [materialUuid] },
            ),
        // Pricing groups (groups.ts): changing one changes the price its items' pricing gives (prices to update).
        /** A fixed price (`basePrice`) or a markup (`tiers`); `materialUuid`: the item it's made from uses it. */
        createGroup: (fields: { name: string; basePrice?: string; tiers?: MarkupTier[] | null; materialUuid?: string }) =>
            optimistic(fields.materialUuid ?? "groups", null, () => api<GroupRow>(`${base}/groups`, { body: fields }), "all"),
        updateGroup: (
            id: string,
            patch: { name?: string; description?: string; basePrice?: string; tax?: GroupTax; tiers?: MarkupTier[] | null },
        ) =>
            optimistic(
                `group:${id}`,
                (d) => ({
                    ...d,
                    groups: d.groups.map((s) =>
                        s.id === id
                            ? {
                                  ...s,
                                  name: patch.name ?? s.name,
                                  description: patch.description ?? s.description,
                                  rule: patch.basePrice === undefined ? s.rule : { ...s.rule, basePrice: patch.basePrice },
                                  tax: patch.tax ?? s.tax,
                                  tiers: patch.tiers === undefined ? s.tiers : patch.tiers,
                              }
                            : s,
                    ),
                }),
                () => api<GroupRow>(`${base}/groups/${id}`, { method: "PATCH", body: patch }),
                "all",
            ),
        deleteGroup: (id: string) => optimistic(`group:${id}`, null, () => api(`${base}/groups/${id}`, { method: "DELETE" }), "all"),
        /** Put an item on a group, or (null) take it off: it keeps the group's rule as its own. */
        useGroup: (uuid: string, groupId: string | null) =>
            optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group`, { method: "PUT", body: { groupId } }), "all"),
        /** A product in a group priced its own way (an exception), or by the group again (groups.ts setOverride). */
        setOverride: (uuid: string, overrides: boolean) => optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group/override`, { method: "PUT", body: { overrides } }), "all"),
        /** Off its group in one step, keeping its price (`keepPrice`: the group gives another). */
        leaveGroup: (uuid: string, keepPrice: boolean) => optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group/leave`, { body: { keepPrice } }), "all"),
        /** Back as it was in one step (groups.ts putBack): on its group (or none), with `ownRule` its own rule, or (null) without one. */
        putBack: (uuid: string, groupId: string | null, ownRule: PricingRule | null) =>
            optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group/put-back`, { body: { groupId, ownRule } }), "all"),

        // Bundles
        createBundle: (data: { name: string; item_number: string; material_list: BundleLine[]; tagIds?: string[]; /** Kept for every try (useNewUuid). */ uuid?: string }) =>
            made("materialbundle", api<BundleRow>(`${base}/bundles`, { body: data })),
        /** A bundle's tags, all at once (as an item's: setItemTags); `updateName`: its name then updated to them. */
        setBundleTags: (uuid: string, tagIds: string[], updateName: boolean) =>
            optimistic(
                uuid,
                (d) => ({ ...d, itemTags: { ...d.itemTags, [uuid]: tagIds } }),
                () => api<BundleRow>(`${base}/bundles/${uuid}/tags`, { method: "PUT", body: { tagIds, updateName } }),
                { materialbundle: [uuid] },
            ),
        updateBundleNameToTags: (uuid: string) => optimistic(uuid, null, () => api<BundleRow>(`${base}/bundles/${uuid}/name/update-to-tags`, { method: "POST" }), { materialbundle: [uuid] }),
        updateBundle: (uuid: string, patch: Partial<Pick<BundleRow, "name" | "item_number" | "material_list" | "active">>) =>
            optimistic(
                uuid,
                (d) => ({ ...d, bundles: d.bundles.map((b) => (b.uuid === uuid ? { ...b, ...patch } : b)) }),
                () => api<BundleRow>(`${base}/bundles/${uuid}`, { method: "PATCH", body: patch }),
                { materialbundle: [uuid] },
            ),

        /** Build an item from products (packages.ts): one sold already (with `product`, one made from what it is first), or a new one. */
        makePackage: (from: { itemUuid: string; product?: { name: string; item_number: string } } | { create: { name: string; item_number: string } }, parts: PackagePart[]) =>
            "itemUuid" in from
                ? optimistic(from.itemUuid, null, () => api<MaterialRow>(`${base}/packages`, { body: { ...from, parts } }), "all")
                : made("material", api<MaterialRow>(`${base}/packages`, { body: { ...from, parts } })),
        /** What an item is built from (its price and cost follow), shown at once. */
        setPackageParts: (uuid: string, parts: PackagePart[]) =>
            optimistic(
                uuid,
                (d) => ({ ...d, packages: { ...d.packages, [uuid]: parts } }),
                () => api<MaterialRow>(`${base}/packages/${uuid}/parts`, { method: "PUT", body: { parts } }),
                { material: [uuid] },
            ),
        /** Make a package an ordinary item again (its price stays, typed). */
        unmakePackage: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/packages/${uuid}`, { method: "DELETE" }), { material: [uuid] }),

        setHistoryIgnored: (priceId: string, entryId: string, ignored: boolean) =>
            api(`${base}/prices/${priceId}/history/${entryId}`, { method: "PATCH", body: { ignored } }).finally(
                () => void qc.invalidateQueries({ queryKey: [...key, "history", priceId] }),
            ),
    };
}

/** A supplier price's change history, newest first. Loaded when it's opened. */
export function usePriceHistory(priceId: string) {
    const company = useCompany();
    return useQuery({
        queryKey: ["c", company.id, "pricelist", "history", priceId],
        queryFn: () => api<PriceHistoryEntry[]>(`/c/${company.id}/tools/pricelist/prices/${priceId}/history`),
    });
}

/** How a quote is taxed: its rate, and that as a fraction. */
export function quoteRate(data: Pick<PriceListData, "taxRates">, quote: TaxQuote) {
    const rate = rateFor(quote.rateUuid, data.taxRates);
    return { rate, tax: fraction(rate) };
}

/** How a supplier price is quoted (its own way, else as its supplier usually quotes), with its rate and that as a fraction. */
export function priceQuote(data: Pick<PriceListData, "taxRates">, price: Pick<SupplierPriceRow, "quote">, supplier: Pick<SupplierRow, "details"> | undefined) {
    const quote = quoteOf(price, supplier?.details);
    return { quote, ...quoteRate(data, quote) };
}

/** How an item's typed cost is typed: as last typed, else before tax at its own rate. */
export function costQuote(data: Pick<PriceListData, "taxRates" | "costQuotes">, m: Pick<MaterialRow, "uuid" | "tax_rate_uuid">): TaxQuote {
    return data.costQuotes[m.uuid] ?? { rateUuid: itemTax(data, m).uuid, includesTax: false };
}

/** Where an item's cost comes from, as the data has it. */
function costSourceOf(data: Pick<PriceListData, "costFollows" | "costPrices">, materialUuid: string): CostSource {
    if (!data.costFollows.includes(materialUuid)) return { from: "typed" };
    const priceId = data.costPrices[materialUuid];
    return priceId ? { from: "price", priceId } : { from: "cheapest" };
}

/** The data with an item's cost coming from `source` (saved, or a draft's: what its Costing tab shows while it's edited). */
export function withCostSource(d: PriceListData, materialUuid: string, source: CostSource): PriceListData {
    const others = d.costFollows.filter((u) => u !== materialUuid);
    const costPrices = Object.fromEntries(Object.entries(d.costPrices).filter(([u]) => u !== materialUuid));
    return {
        ...d,
        costFollows: source.from === "typed" ? others : [...others, materialUuid],
        costPrices: source.from === "price" ? { ...costPrices, [materialUuid]: source.priceId } : costPrices,
    };
}

/**
 * An item's cost being changed, not saved yet (its Costing tab: kept by its window, as its pricing's draft): where it comes from,
 * and (typed in) what's typed, as quoted. Saved by its save bar: Save & update (its cost too), Save only, or (typed) Save.
 */
export interface CostDraft {
    source: CostSource;
    typed?: { amount: string; quote: TaxQuote };
}

/**
 * The cost a cost draft gives, in the item's basis: as typed, or the supplier
 * price it would follow (as the server works it out: supplierPrices.ts costUpdates); else as it is.
 */
export function draftPartCost(data: PriceListData, item: MaterialRow, draft: CostDraft): string {
    const { tax } = itemTax(data, item);
    if (draft.source.from === "typed") {
        if (!draft.typed) return item.cost;
        const { amount, quote } = draft.typed;
        return costFromQuoted(amount, quote, quoteRate(data, quote).tax, item, tax, data.settings);
    }
    const view = withCostSource(data, item.uuid, draft.source);
    const pick = itemSupplierPrices(view, item.uuid).cost?.pick;
    return pick ? itemCost(pick.real, item, tax) : item.cost;
}

/** An item's supplier prices, cheapest real cost first, and (when its cost follows one) the price it follows and why. */
export function itemSupplierPrices(data: PriceListData, materialUuid: string) {
    const suppliers = new Map(data.suppliers.map((s) => [s.uuid, s]));
    const ranked = byRealCost(
        data.supplierPrices.filter((p) => p.materialUuid === materialUuid),
        (p) => priceQuote(data, p, suppliers.get(p.supplierUuid)).tax,
        data.settings,
    );
    const source = costSourceOf(data, materialUuid);
    const chosen = source.from === "price" ? source.priceId : null;
    const cost = source.from === "typed" ? null : costPrice(ranked, chosen);
    return { ranked, source, cost };
}

/**
 * An item's cost as its Costing tab shows it: the supplier price it follows exactly as quoted (and
 * which supplier), or its typed cost exactly as typed. Never read back from ServiceM8's cost, which
 * is kept to the cent in the item's own basis. The Pricing tab shows the same.
 */
export function itemCostShown(data: PriceListData, item: MaterialRow): { amount: string; quote: TaxQuote; from: string | null } {
    const pick = itemSupplierPrices(data, item.uuid).cost?.pick;
    if (pick) {
        const supplier = data.suppliers.find((x) => x.uuid === pick.price.supplierUuid);
        const followed = priceQuote(data, pick.price, supplier);
        return { amount: quotedPrice(pick.price.priceNet, followed.quote, followed.tax), quote: followed.quote, from: supplier?.name ?? "a supplier" };
    }
    const quote = costQuote(data, item);
    const { tax } = itemTax(data, item);
    const typed = data.costTyped[item.uuid] ?? null;
    // Saved only (waiting: its cost to update is what it gives): as typed, not ServiceM8's.
    const waiting = data.costUpdates[item.uuid];
    if (typed !== null && waiting !== undefined && costFromQuoted(typed, quote, quoteRate(data, quote).tax, item, tax, data.settings) === waiting) {
        return { amount: toCents(toNumber(typed)), quote, from: null };
    }
    const amount = shownTypedCost(item, tax, quote, quoteRate(data, quote).tax, data.settings, typed);
    return { amount, quote, from: null };
}
